/* eslint-disable no-restricted-syntax */
import React from 'react';
import { useRouter } from 'next/router';
import { isAndroidAppWebView, isIosAppWebView } from '@kcd/app-interface';

import { getRouterParams } from '~/libs/nextjs';
import { PRODUCT_TYPE_E_COUPON } from '~/pages/e-coupon/constants';
import {
  closeCurrentModal,
  isLoadedInAppHavingMarketTab,
  openPageModal,
} from '~/utils/mobileApp';
import { isServer } from '~/utils/ssr';
import { toSearchObject } from '~/utils/url';

import { getHistoryIndexByKey } from './history';

export function useRouterIsReady() {
  const router = useRouter();
  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    setIsReady(router.isReady);
  }, [router.isReady]);

  return isReady;
}

function useRouterQuery() {
  const router = useRouter();
  const isRouterReady = useRouterIsReady();

  return React.useMemo(
    () =>
      isServer() || isRouterReady
        ? router.query
        : (() => {
            return {
              ...getRouterParams({
                nextPathname: router.pathname,
                pathname: window.location.pathname,
              }),
              ...toSearchObject(new URLSearchParams(window.location.search)),
            };
          })(),
    [isRouterReady, router.pathname, router.query],
  );
}

function useRouterQueryByKey(key: string, type: 'string'): string;
function useRouterQueryByKey(key: string, type: 'array'): string[];
function useRouterQueryByKey(key: string, type: 'number'): number;
function useRouterQueryByKey(key: string, type: 'string' | 'array' | 'number') {
  const queries = useRouterQuery();
  const query = queries[key];
  if (type === 'string') {
    return query?.toString() || '';
  }

  if (type === 'array') {
    return typeof query === 'string' ? [query] : query;
  }

  if (type === 'number') {
    return Number(query);
  }

  return query;
}

function useUpdateRouterQuery() {
  const router = useRouter();
  const query = useRouterQuery();

  return (params: Record<string, string | string[] | undefined>) => {
    const nextQuery = { ...query, ...params };
    Object.keys(nextQuery).forEach(
      (key) => nextQuery[key] === undefined && delete nextQuery[key],
    );

    return router.replace(
      {
        pathname: router.pathname,
        query: nextQuery,
      },
      undefined,
      { shallow: true },
    );
  };
}

// URL이 http 또는 https로 시작하는지 확인하는 함수
function isAbsoluteUrl(url: string): boolean {
  return /^(http|https)?:\/\//.test(url);
}

function usePushWithQuery() {
  const router = useRouter();

  return (nextUrl: string, isPush = true) => {
    const nextPath = `${nextUrl}${window.location.search}`;

    if (!isAbsoluteUrl(nextUrl) && isPush) {
      return router.push(nextPath);
    }

    return openPageModal(nextPath);
  };
}

// 인앱 마켓탭을 가지고 있는 앱이라면 home으로 갈때 closeCurrentModal을 부르고, 그렇지 않으면 fallback을 부른다.
function useGoHome(method: 'replace' | 'push') {
  const router = useRouter();

  return () => {
    if (isLoadedInAppHavingMarketTab()) {
      closeCurrentModal();
      return;
    }

    void router[method]('/');
  };
}

function useGoBack() {
  const router = useRouter();
  const productType = useRouterQueryByKey('productType', 'string');

  return () => {
    const { key } = (window.history.state as { key: string }) || '';

    const historyIndex = getHistoryIndexByKey(key);

    // historyState가 없는 경우에
    // 앱 내 웹뷰일 경우는 앱에 요청 / 그렇지 않으면 홈화면으로 이동함. (모바일웹)
    if (!historyIndex || historyIndex === 0) {
      if (isIosAppWebView() || isAndroidAppWebView()) {
        closeCurrentModal();
        return;
      }

      if (productType === PRODUCT_TYPE_E_COUPON) {
        void router.replace('/e-coupon');
      } else {
        void router.replace('/app');
      }
      return;
    }

    router.back();
  };
}

function usePagePrefetch(...page: string[]) {
  const router = useRouter();
  React.useEffect(() => {
    page.forEach((url) => {
      void router.prefetch(url);
    });
  }, [page, router]);
}

export {
  useGoBack,
  useGoHome,
  usePagePrefetch,
  usePushWithQuery,
  useRouterQuery,
  useRouterQueryByKey,
  useUpdateRouterQuery,
};
